import DeparturePicker from "./DeparturePicker";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, LockKeyhole, Plane } from "lucide-react";
import { cachedDealKey, cachedDealLoginLink, cheapestCachedDeals } from "@/lib/flightHighlights";
import "@/styles/cheapDealShelf.css";

const money = (value, currency) => new Intl.NumberFormat("en-IN", { style: "currency", currency: currency || "INR", maximumFractionDigits: 0 }).format(value);
const dateLabel = value => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return "Date unavailable";
  return new Date(`${value}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

export function CheapDealCard({ deal, origin, currency, showDates = false, selected = false }) {
  let bookingUrl = "";
  try {
    const url = new URL(deal.liveFareUrl);
    if (url.protocol === "https:") bookingUrl = url.href;
  } catch {}
  const weeks = deal.tripWeeks || (deal.tripDays >= 10 ? 2 : 1);
  return <article className={`small-fare-card ${selected ? "small-fare-selected" : ""}`} data-deal-id={deal.dealId}>
    <div className="small-fare-photo">
      {deal.destinationImage ? <img src={deal.destinationImage} alt="" loading="lazy" onError={event => { event.currentTarget.style.display = "none"; }} /> : <Plane className="small-fare-placeholder" size={48} strokeWidth={1} aria-hidden="true" />}
      <span className="small-fare-roundtrip">Round trip</span>
      <div className="small-fare-place"><h3>{deal.destinationName}</h3><p>{deal.destinationCountry}</p></div>
    </div>
    <div className="small-fare-body">
      {showDates ? Number(deal.averagePrice) > Number(deal.price) && <p className="small-fare-average"><s>{money(deal.averagePrice, currency)}</s> <span>(average)</span></p> : <p className="small-fare-label">Lowest found</p>}
      <p className="small-fare-price">{money(deal.price, currency)}<span> / person</span></p>
      <p className="small-fare-duration" title={`${deal.tripDays}-day round trip`}>{weeks}-week round trip</p>
      {showDates && <div className="small-fare-dates"><p><Plane size={14} aria-hidden="true" /><span>Depart <strong>{dateLabel(deal.departureDate)}</strong></span></p><p><CalendarDays size={14} aria-hidden="true" /><span>Return <strong>{dateLabel(deal.returnDate)}</strong></span></p></div>}
      {!showDates ? <Link className="small-fare-action" to={cachedDealLoginLink(origin, deal)} aria-label={`Sign in to view ${deal.destinationName} ${weeks}-week deal`}><ArrowRight size={21} /></Link>
        : bookingUrl ? <a className="small-fare-book" href={bookingUrl} target="_blank" rel="noreferrer">Check fare <ArrowRight size={15} /></a> : <p className="small-fare-book-note">Use the search to check booking options.</p>}
    </div>
  </article>;
}

const preferenceKey = "trailpulse:small-fares-departure-metro";

export default function CheapDealShelf() {
  const [origin, setOrigin] = useState(() => {
    try { return localStorage.getItem(preferenceKey) || "BOM"; } catch { return "BOM"; }
  });
  const [metros, setMetros] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setSnapshot(null);
    setFailed(false);
    api.get(`/public/flight-deals?origin=${encodeURIComponent(origin)}`, { signal: controller.signal })
      .then(({ data }) => {
        if (!controller.signal.aborted) { setSnapshot(data); setMetros(data.metros || []); }
      }).catch(() => { if (!controller.signal.aborted) setFailed(true); });
    return () => controller.abort();
  }, [origin]);
  const selected = metros.find(metro => metro.code === origin);
  const data = snapshot?.origin === origin ? snapshot : null;
  const originName = data?.originName || selected?.name || (origin === "BOM" ? "Mumbai" : origin);
  const feed = { ...data, origin, originName, loading: !data && !failed, failed };
  const changeOrigin = value => {
    setOrigin(value);
    try { localStorage.setItem(preferenceKey, value); } catch {}
  };
  const deals = cheapestCachedDeals(feed?.deals, 4);
  return <section className="small-fare-section" aria-labelledby="small-fares-heading">
    <div className="small-fare-heading"><div><p className="small-fare-eyebrow">A little less on flights. A little more on the trip.</p><h2 id="small-fares-heading">Small fares. Big getaway energy.</h2><p>A few lower-priced round trips from {feed?.originName || "your departure city"}.</p></div><div className="small-fare-origin"><DeparturePicker id="small-fares-departure" label="Small fares departure city" origin={origin} originName={originName} metros={metros} onChange={changeOrigin} /></div></div>
    {feed?.loading || !feed ? <div className="small-fare-grid" role="status">{[0, 1, 2, 3].map(key => <div key={key} className="small-fare-skeleton" />)}<span className="sr-only">Loading affordable round trips</span></div>
      : deals.length ? <div className="small-fare-grid">{deals.map(deal => <CheapDealCard key={cachedDealKey(deal)} deal={deal} origin={feed.origin} currency={feed.currency} />)}</div>
        : <p className="small-fare-empty" role="status">{feed.failed ? "We couldn’t load these fares. Try again shortly." : `Lower-priced round trips from ${feed.originName} will appear when fares are available. Choose another departure city in this section to explore.`}</p>}
    <div className="small-fare-footnote"><p><LockKeyhole size={15} aria-hidden="true" />Sign in to explore dates and flight options.</p><p>Round-trip prices per person. Fares may change.{feed?.stale ? " Showing previously checked fares." : ""}</p></div>
  </section>;
}
